import { StudioMetadata } from "interfaces/ClientStudio.interface";
import { Attribute, Data, Metadata } from "interfaces/Metadata.interface";

class MetadataBuilder {
  private name: string;
  private attributes: Attribute[];
  private image: string;
  private animationUrl: string | null;
  private description: string;
  private externalUrl: string;
  private data: Data | null;
  private modelHash: string;
  private redeemLimitDate: Date | string | null;

  constructor() {
    this.name = "";
    this.image = "";
    this.animationUrl = null;
    this.description = "";
    this.externalUrl = "";
    this.data = null;
    this.modelHash = "";
    this.redeemLimitDate = null;
    this.attributes = [];
  }

  setName(name: string): MetadataBuilder {
    this.name = name;
    return this;
  }

  setImage(image: string): MetadataBuilder {
    this.image = image;
    return this;
  }

  setAnimationUrl(animationUrl: string): MetadataBuilder {
    this.animationUrl = animationUrl;
    return this;
  }

  setDescription(description: string): MetadataBuilder {
    this.description = description;
    return this;
  }

  setExternalUrl(externalUrl: string): MetadataBuilder {
    this.externalUrl = externalUrl;
    return this;
  }

  addAttribute(trait_type: string, value: string): MetadataBuilder {
    this.attributes.push({
      trait_type: trait_type.toUpperCase(),
      value: value.toUpperCase(),
    });
    return this;
  }

  addAttributes(
    attrs: { trait_type: string; value: string }[],
  ): MetadataBuilder {
    this.attributes = [
      ...this.attributes,
      ...attrs.map((attr) => ({
        trait_type: attr.trait_type.toUpperCase(),
        value: attr.value.toUpperCase(),
      })),
    ];
    return this;
  }

  addData(front: string, back?: string): MetadataBuilder {
    this.data = {
      front,
    };
    if (back && this.data) {
      this.data.back = back;
    }
    return this;
  }

  setModelHash(modelHash: string): MetadataBuilder {
    this.modelHash = modelHash;
    return this;
  }

  build(): Metadata {
    const result: Metadata = {
      name: this.name,
      image: this.image,
      description: this.description,
      external_url: this.externalUrl,
      attributes: this.attributes,
      modelHash: this.modelHash,
    };
    if (this.data) {
      result.data = this.data;
    }
    if (this.animationUrl) {
      result.animation_url = this.animationUrl;
    }
    return result;
  }

  getAttributes(metadata: Metadata): StudioMetadata {
    const partnerValue = metadata.attributes.find((attribute) =>
      ["Partner", "PARTNER"].includes(attribute.trait_type),
    );
    const colorValue = metadata.attributes.find((attribute) =>
      ["Color", "COLOR"].includes(attribute.trait_type),
    );
    const typeValue = metadata.attributes.find((attribute) =>
      ["Physical item", "PHYSICAL ITEM"].includes(attribute.trait_type),
    );
    const redeemLimitDate = metadata.attributes.find((attribute) =>
      ["Final redeem date", "FINAL REDEEM DATE"].includes(attribute.trait_type),
    );

    const customAttributes = metadata.attributes.filter(
      (attribute) =>
        ![
          "Partner",
          "PARTNER",
          "Color",
          "COLOR",
          "Physical item",
          "PHYSICAL ITEM",
          "Final redeem date",
          "FINAL REDEEM DATE",
        ].includes(attribute.trait_type),
    );

    return {
      metadataPartner: partnerValue?.value ?? null,
      metadataColor: colorValue?.value ?? null,
      metadataType: typeValue?.value ?? null,
      metadataFinalRedeemDate: redeemLimitDate?.value ?? null,
      metadataAttributes: customAttributes,
    };
  }
}

export default MetadataBuilder;
